import { Navigate, Route, Routes, useLocation } from 'react-router';
import { Rights } from '@aiinterview/shared';
import { useSession } from './api/session';
import { Layout } from './components/Layout';
import { Loading } from './components/ui';
import { Login } from './pages/Login';
import { ChangePassword } from './pages/ChangePassword';
import { Dashboard } from './pages/Dashboard';
import { Candidates } from './pages/Candidates';
import { CandidateEdit } from './pages/CandidateEdit';
import { CandidateInterviews } from './pages/CandidateInterviews';
import { Import } from './pages/Import';
import { Criteria } from './pages/Criteria';
import { Questions } from './pages/Questions';
import { Templates } from './pages/Templates';
import { Proctoring } from './pages/Proctoring';
import { ProctoringEdit } from './pages/ProctoringEdit';
import { Users } from './pages/Users';
import { Settings } from './pages/Settings';
import { Audit } from './pages/Audit';
import { Integrations } from './pages/Integrations';
import { Interviews } from './pages/Interviews';
import { Interactions } from './pages/Interactions';
import { InteractionReport } from './pages/InteractionReport';
import { InteractionFormats } from './pages/InteractionFormats';
import { InterviewCreate } from './pages/InterviewCreate';
import { Report } from './pages/Report';
import { Denied } from './pages/Denied';

/**
 * Gates a route on a right.
 *
 * This is presentation only. Every route it guards is gated again on the server, which is the check
 * that actually matters - a hidden link is not a permission.
 */
function Guarded({ right, children }: { right: string; children: React.ReactNode }) {
    const { can } = useSession();
    return can(right) ? <>{children}</> : <Denied />;
}

export function App() {
    const { user, loading } = useSession();
    const location = useLocation();

    if (loading) {
        return <Loading what="Signing in" />;
    }

    if (!user) {
        return (
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        );
    }

    // A required password change blocks everything else. Otherwise the seeded administrator could
    // navigate away and carry on using a password that was printed to a log.
    if (user.mustChangePassword && location.pathname !== '/change-password') {
        return <Navigate to="/change-password" replace />;
    }

    return (
        <Layout>
            <Routes>
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/change-password" element={<ChangePassword />} />

                <Route path="/" element={<Guarded right={Rights.View}><Dashboard /></Guarded>} />

                <Route path="/interviews" element={<Guarded right={Rights.View}><Interviews /></Guarded>} />
                <Route path="/interviews/new" element={<Guarded right={Rights.Create}><InterviewCreate /></Guarded>} />
                <Route path="/interviews/:id" element={<Guarded right={Rights.View}><Report /></Guarded>} />

                <Route path="/surveys" element={<Guarded right={Rights.View}><Interactions kind="Survey" /></Guarded>} />
                <Route path="/surveys/:id" element={<Guarded right={Rights.View}><InteractionReport /></Guarded>} />
                <Route path="/listening" element={<Guarded right={Rights.View}><Interactions kind="Listening" /></Guarded>} />
                <Route path="/listening/:id" element={<Guarded right={Rights.View}><InteractionReport /></Guarded>} />

                <Route path="/candidates" element={<Guarded right={Rights.View}><Candidates /></Guarded>} />
                <Route path="/candidates/import" element={<Guarded right={Rights.Import}><Import /></Guarded>} />
                <Route path="/candidates/:id" element={<Guarded right={Rights.View}><CandidateEdit /></Guarded>} />
                <Route path="/candidates/:id/interviews" element={<Guarded right={Rights.View}><CandidateInterviews /></Guarded>} />

                <Route path="/criteria" element={<Guarded right={Rights.Configure}><Criteria /></Guarded>} />
                <Route path="/questions" element={<Guarded right={Rights.Configure}><Questions /></Guarded>} />
                <Route path="/templates" element={<Guarded right={Rights.Configure}><Templates /></Guarded>} />
                <Route path="/survey-formats" element={<Guarded right={Rights.Configure}><InteractionFormats kind="Survey" /></Guarded>} />
                <Route path="/listening-formats" element={<Guarded right={Rights.Configure}><InteractionFormats kind="Listening" /></Guarded>} />
                <Route path="/proctoring" element={<Guarded right={Rights.Configure}><Proctoring /></Guarded>} />
                <Route path="/proctoring/:id" element={<Guarded right={Rights.Configure}><ProctoringEdit /></Guarded>} />
                <Route path="/settings" element={<Guarded right={Rights.Configure}><Settings /></Guarded>} />

                <Route path="/users" element={<Guarded right={Rights.ManageUsers}><Users /></Guarded>} />
                <Route path="/integrations" element={<Guarded right={Rights.ManageUsers}><Integrations /></Guarded>} />
                <Route path="/audit" element={<Guarded right={Rights.ManageUsers}><Audit /></Guarded>} />

                <Route path="*" element={<Denied notFound />} />
            </Routes>
        </Layout>
    );
}
